import { ApiShipmentType } from "entities/shared/shipmentCard.types";
import { backofficeClient } from "shared/api/backofficeClient";

import type {
  FollowCardQuery,
  GetCardsQuery,
  SeaWhereQuery,
  UpdateCardData,
  UpdateCardQuery,
  UpdateCardsQuery,
} from "./query.types";
import type {
  DeleteCardResponseDTO,
  SavedShipmentResponseDataDTO,
  SavedShipmentResponseDTO,
  UpdateCardsResponseDTO,
} from "./response.types";

/**
 * Saved shipments live in the back office and are authenticated by the
 * session cookie. They used to be addressed on the marketing site under
 * /tracking-system, which never served them.
 *
 * Ocean and air cards share one table there, so every read pins
 * `shipment_type`: without it the ocean panel would list air waybills too.
 */
const userRequestInstance = backofficeClient;

const seaOnly = (where?: SeaWhereQuery): SeaWhereQuery => ({
  ...where,
  shipment_type: ApiShipmentType.SEA,
});

class SavedShipmentApi {
  public getCards(body?: GetCardsQuery, signal?: AbortSignal): Promise<SavedShipmentResponseDTO> {
    const defaultParams: Partial<GetCardsQuery> = {
      limit: null,
      offset: null,
      order: [{ column: "created_at", asc: false }],
    };
    const data: GetCardsQuery = {
      ...defaultParams,
      ...body,
      where: seaOnly(body?.where),
      order: [...(body?.order ?? []), ...(defaultParams.order ?? [])],
    };
    return userRequestInstance.post<SavedShipmentResponseDTO, SavedShipmentResponseDTO, GetCardsQuery>(
      "/api/shipments/get",
      data,
      { ...(signal ? { signal } : {}) }
    );
  }

  public updateCard({ id, data }: UpdateCardQuery): Promise<SavedShipmentResponseDataDTO> {
    return userRequestInstance.put<SavedShipmentResponseDataDTO, SavedShipmentResponseDataDTO, UpdateCardData>(
      `/api/shipments/${id}`,
      data
    );
  }

  public updateCards(data: UpdateCardsQuery): Promise<UpdateCardsResponseDTO> {
    return userRequestInstance.post<UpdateCardsResponseDTO, UpdateCardsResponseDTO, UpdateCardsQuery>(
      "/api/shipments/update",
      { ...data, where: seaOnly(data.where) }
    );
  }

  public deleteCard(id: number): Promise<DeleteCardResponseDTO> {
    return userRequestInstance.delete<DeleteCardResponseDTO, DeleteCardResponseDTO>(`/api/shipments/${id}`);
  }

  public followCard(data: FollowCardQuery): Promise<SavedShipmentResponseDataDTO> {
    return userRequestInstance.post<SavedShipmentResponseDataDTO, SavedShipmentResponseDataDTO, FollowCardQuery>(
      "/api/shipments",
      data
    );
  }
}

const savedShipmentService = new SavedShipmentApi();

export { savedShipmentService };
